/**
 * /{ws}/activity — the Activity feed (core logic/activity + ActivityViewModel): mentions, replies in threads I follow,
 * reactions to my messages, channels I was added to, and (owners/admins) people who joined through an allowed email
 * domain. Filters (All / Mentions / Threads / Reactions), unread dots, "Mark all as read"; a click marks the item
 * read and opens the message in context (its thread or conversation, scrolled to it). Needs the settings API.
 */
import { Activity, Features, Profile, Shell, type ActivityFilter } from "@bundle/core";
import { Avatar, Badge, Button, ComingSoon, focusMessage, Icon, Pressable, ScrollView, SegmentedControl, Spinner, Stack, Text, useFormat, useNavigation, useFeatureAccess, useSettingsContext, useT, useTr, useViewModel, useWorkspace } from "@bundle/ui";

export function ActivityScreen() {
  const settings = useSettingsContext();
  const vm = settings?.activity;
  if (vm === undefined) return <ActivityEmpty />;
  return <ActivityFeed vm={vm} />;
}

function ActivityEmpty() {
  const t = useT();
  const { state } = useWorkspace();
  const members = state.cache?.members ?? {};
  const role = state.cache?.meId === undefined ? undefined : members[state.cache.meId]?.role;
  const admin = role !== undefined && Activity.canSeeActivity("member_joined", role);
  return <ComingSoon icon="bell" title={t("activity.title")} body={t(admin ? "activity.adminEmpty" : "activity.empty")} />;
}

function ActivityFeed({ vm }: { vm: NonNullable<ReturnType<typeof useSettingsContext>>["activity"] & object }) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const nav = useNavigation();
  const { state: ws, slug } = useWorkspace();
  const s = useViewModel(vm);
  const access = useFeatureAccess();

  if (s.status === "loading") {
    return (
      <Stack grow align="center" justify="center" testID="activity-loading">
        <Spinner />
      </Stack>
    );
  }
  if (s.status === "failed") {
    return (
      <Stack grow align="center" justify="center" gap="3" padding="8" testID="activity-failed">
        <Text color="text.secondary" align="center">
          {t("activity.failed")}
        </Text>
        <Button variant="secondary" onPress={() => vm.retry()} label={t("activity.retry")} testID="activity-retry" />
      </Stack>
    );
  }
  if (!s.items.length) return <ActivityEmpty />;

  const members = ws.cache?.members ?? {};
  const channels = ws.cache?.channels ?? {};
  // Items from areas an admin switched off (core logic/features) leave the feed until they're back on.
  const rows = Activity.activityRows(s.items, members, channels, slug, s.filter).filter((row) => {
    const area = Features.featureOfRoute(row.route);
    return area === undefined || access(area) !== "none";
  });
  const filters = Activity.ACTIVITY_FILTERS.map((f) => ({
    value: f,
    label: t(`activity.filter.${f}`),
    count: f === "all" ? undefined : s.items.filter((it) => it.unread === true && Activity.matchesFilter(it.kind, f)).length || undefined,
  }));

  return (
    <Stack grow testID="activity-screen">
      <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="8" paddingY="4">
        <Text variant="title2" weight="bold">
          {t("activity.title")}
        </Text>
        <Button variant="ghost" size="sm" icon="check" label={t("activity.markAllRead")} disabled={s.unread === 0} onPress={() => vm.markAllRead()} testID="activity-mark-all" />
      </Stack>
      <Stack paddingX="8" paddingY="1">
        <SegmentedControl<ActivityFilter> options={filters} value={s.filter} onChange={(f) => vm.setFilter(f)} variant="pills" size="sm" accessibilityLabel={t("activity.filters")} testID="activity-filter" />
      </Stack>
      <ScrollView>
        <Stack paddingX="5" paddingY="2" gap="1">
          {rows.length === 0 ? (
            <Stack padding="8" align="center">
              <Text color="text.secondary">{t("activity.filteredEmpty")}</Text>
            </Stack>
          ) : null}
          {rows.map((row) => {
            const m = members[row.userId];
            const look = m === undefined ? {} : Profile.avatarLook(m);
            const text = tr(row.text);
            const open = () => {
              vm.open(row.id);
              nav.open(Shell.formatUrl(row.route));
              if (row.focusMessageId !== undefined) focusMessage(row.focusMessageId);
            };
            return (
              <Pressable
                key={row.id}
                onPress={open}
                direction="horizontal"
                radius="md"
                accessibilityLabel={row.unread ? `${t("activity.unread")}: ${text}` : text}
                testID={`activity-row-${row.id}`}
              >
                <Stack direction="horizontal" align="start" gap="3" paddingX="3" paddingY="2" grow background={row.unread ? "bg.selected" : undefined} radius="md">
                  {row.kind === "reminder" || row.kind === "event_reminder" ? (
                    <Stack align="center" justify="center" testID={`activity-reminder-${row.id}`}>
                      <Icon name={row.kind === "event_reminder" ? "calendar" : "clock"} size="icon.lg" color="accent.text" />
                    </Stack>
                  ) : (
                    <Avatar name={m?.fullName || m?.displayName || "?"} src={look.src} tone={look.tone} emoji={look.emoji} size="avatar.md" />
                  )}
                  <Stack grow gap="1">
                    <Text weight={row.unread ? "semibold" : undefined}>
                      {text}
                    </Text>
                    {row.preview === undefined || row.preview === "" ? null : (
                      <Text color="text.secondary" lines={2}>
                        {row.preview}
                      </Text>
                    )}
                    <Text variant="caption" color="text.tertiary">
                      {fmt.dateTime(row.ts)}
                    </Text>
                  </Stack>
                  {row.unread ? <Badge dot tone="accent" accessibilityLabel={t("activity.unread")} /> : null}
                </Stack>
              </Pressable>
            );
          })}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
